import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import PageHeader from '../components/PageHeader.jsx';
import IntegrationsForm from '../components/IntegrationsForm.jsx';
import MailTemplateEditor from '../components/MailTemplateEditor.jsx';
import SendingDomainCard from '../components/SendingDomainCard.jsx';
import LinkDomainCard from '../components/LinkDomainCard.jsx';
import SharedDatabasesCard from '../components/SharedDatabasesCard.jsx';
import PersonalAiConnectionsCard from '../components/PersonalAiConnectionsCard.jsx';
import SharedSuitesCard from '../components/SharedSuitesCard.jsx';
import FanOwlAdmin from '../components/FanOwlAdmin.jsx';
import MyOwlMemory from '../components/MyOwlMemory.jsx';
import OwlAddressCard from '../components/OwlAddressCard.jsx';
import ApiKeysCard from '../components/ApiKeysCard.jsx';
import DashboardDraftsCard from '../components/DashboardDraftsCard.jsx';
import WorkspaceKeyCard from '../components/WorkspaceKeyCard.jsx';
import DriveSourcesCard from '../components/DriveSourcesCard.jsx';
import LeadFeedsCard from '../components/LeadFeedsCard.jsx';
import WebsiteHub from '../components/website/WebsiteHub.jsx';
import MetaConnectCard from '../components/MetaConnectCard.jsx';
import TikTokConnectCard from '../components/TikTokConnectCard.jsx';
import XConnectCard from '../components/XConnectCard.jsx';
import LinkedInConnectCard from '../components/LinkedInConnectCard.jsx';
import QueueItCard from '../components/QueueItCard.jsx';
import MailLogView from '../components/MailLogView.jsx';
import BillingCard from '../components/BillingCard.jsx';
import NotificationPrefs from '../components/NotificationPrefs.jsx';
import TwoFactorCard from '../components/TwoFactorCard.jsx';
import TeamManager from '../components/TeamManager.jsx';
import RateCard from '../components/RateCard.jsx';
import SuiteDashboardsEditor from '../components/SuiteDashboardsEditor.jsx';
import NavHiddenEditor from '../components/NavHiddenEditor.jsx';
import ShareLinksManager from '../components/ShareLinksManager.jsx';
import Icon from '../components/Icon.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useMyFlags, flagOn } from '../lib/flags.js';
import { useProfile } from '../lib/profile.jsx';
import { useAccess, PERMS } from '../lib/access.js';
import { useAuth } from '../lib/auth.jsx';
import { fanOwlSettingsEnabled } from '../lib/features.js';

// Client self-service Settings — one place for everything the client manages
// themselves, organised by a section nav: Integrations, Branding, CC-the-Owl.
// (Digests + Actions are first-class pages in the left nav, not settings.)
// Each section declares the permission that reveals it; Notifications is
// personal so it's always available.
const SECTIONS = [
  ['team', 'Team', 'clients', PERMS.TEAM_MANAGE],
  ['dashboards', 'Dashboards', 'dashboards', PERMS.CONTENT_MANAGE],
  ['hidden', 'Hidden reports', 'eyeoff', null],
  ['sharelinks', 'Share links', <Icon key="sharelinks" name="links" size={15} />, PERMS.SHARE_MANAGE],
  ['website', 'Website', 'campaigns', PERMS.CAMPAIGNS_VIEW], // contact forms, newsletter sign-ups, pop-ups (components/website/WebsiteHub.jsx)
  ['shareddb', 'Shared databases', 'plug', PERMS.CAMPAIGNS_VIEW],
  ['sharedsuites', 'Shared events', 'dashboards', PERMS.DASHBOARDS_VIEW],
  ['integrations', 'Integrations', 'plug', PERMS.INTEGRATIONS_MANAGE],
  ['notifications', 'Notifications', 'alerts', null],
  ['security', 'Security', 'lock', null],
  ['email', 'Branding', 'palette', PERMS.BRANDING_MANAGE],
  ['billing', 'Billing', 'card', PERMS.INTEGRATIONS_MANAGE],
  ['fees', 'Messaging rates', 'card', PERMS.CAMPAIGNS_VIEW],
  ['sentmail', 'Sent emails', 'email', PERMS.INTEGRATIONS_MANAGE],
  ['inbox', 'CC the Owl', 'inbox', PERMS.INTEGRATIONS_MANAGE],
  ['fanowl', 'Fan Owl', 'owl', PERMS.INTEGRATIONS_MANAGE],
  ['owlmemory', 'Owl memory', 'insights', null],
];

export default function ClientIntegrationsPage() {
  const isMobile = useIsMobile();
  const { can, role, isAdmin } = useAccess();
  const { user: authUser } = useAuth();
  const { active } = useProfile(); // every section is scoped to the active client profile
  const ctx = useOutletContext() || {};
  const [items, setItems] = useState(null);
  // Settings only ever show the profile in context — switching profile (top header)
  // is how you reach another client's settings. Never list multiple clients at once.
  // For an admin PREVIEWING a client (console mode) there's no `active` profile,
  // so fall back to the entity the shell resolved (previewEntityId) — that's the
  // client they opened — so admins who own the account can manage its settings.
  const scopeId = active?.id || ctx.previewEntityId || null;
  const activeItem = items && scopeId ? items.find((it) => it.entityId === scopeId) : null;
  // The entity (id + name) the page acts on — from the profile if present, else
  // derived from the resolved settings row.
  const ent = active || (activeItem ? { id: activeItem.entityId, name: activeItem.name } : null);
  // Only the sections this role can use (Notifications is personal, always on).
  // Fan Owl is dogfood-gated to allowlisted accounts (server enforces the same).
  // selfservice.* flags decide which sections a CLIENT may manage themselves.
  const myFlags = useMyFlags(scopeId);
  const SECTION_FLAG = { team: 'selfservice.team', dashboards: 'selfservice.dashboards', hidden: 'navvisibility', sharelinks: 'sharelinks', integrations: 'selfservice.integrations', email: 'selfservice.branding', fanowl: 'fanowl', shareddb: 'engage.shared_databases', sharedsuites: 'engage.shared_suites', billing: 'billing.self_service' };
  // Website shows when any of its three parts is on for this client.
  const webParts = { contact: flagOn(myFlags, 'engage.contact_forms'), newsletter: flagOn(myFlags, 'engage.newsletter_forms'), promos: flagOn(myFlags, 'engage.site_promos') };
  const sections = SECTIONS.filter(([key, , , perm]) => (!perm || can(perm)) && (key !== 'fanowl' || fanOwlSettingsEnabled(authUser))
    && (key === 'website' ? Object.values(webParts).some(Boolean) : flagOn(myFlags, SECTION_FLAG[key] || '')));
  // Deep link: /settings?section=integrations|team|notifications|email… opens that
  // section (used by the onboarding "Go" buttons). Falls back to the first allowed.
  const [params, setParams] = useSearchParams();
  // The old ?section=newsletter|contact|promos links open Website on that part.
  const asked = params.get('section');
  const webPart = { newsletter: 'newsletter', contact: 'contact', promos: 'promos' }[asked] || '';
  const requested = webPart ? 'website' : asked;
  const [section, setSection] = useState(
    requested && sections.some(([k]) => k === requested) ? requested : (sections[0]?.[0] || 'notifications'),
  );
  useEffect(() => { api.getMyIntegrations().then(setItems).catch(() => setItems([])); }, []);
  // ?focus=meta-ads|social (the Sales Command Centre's "Connect" buttons): scroll
  // straight to that connection and flash it, instead of leaving people to hunt.
  // The Meta card covers both; without the platform Meta app it renders nothing,
  // so fall back to the pasted-token form above it.
  const focus = params.get('focus');
  const focusReady = section === 'integrations' && !!activeItem;
  useEffect(() => {
    if (!focus || !focusReady) return undefined;
    const t = setTimeout(() => {
      const card = document.getElementById('int-meta');
      const el = card && card.offsetHeight ? card : document.getElementById('int-form');
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.style.transition = 'box-shadow .3s'; el.style.boxShadow = '0 0 0 3px var(--accent)';
      setTimeout(() => { el.style.boxShadow = ''; }, 2500);
    }, 400);
    return () => clearTimeout(t);
  }, [focus, focusReady]);
  // Follow ?section= when it changes while you're already here (the sidebar's
  // Fan Owl item deep-links into Settings), and once the flags
  // load (on first render the section may not be in the list yet).
  const sectionKeys = sections.map(([k]) => k).join();
  useEffect(() => { if (requested && sections.some(([k]) => k === requested)) setSection(requested); }, [requested, sectionKeys]); // eslint-disable-line react-hooks/exhaustive-deps
  // If the active section isn't permitted (e.g. after a profile switch), fall back.
  useEffect(() => { if (!sections.some(([k]) => k === section)) setSection(sections[0]?.[0] || 'notifications'); }, [sections, section]);

  return (
    <main style={{ flex: 1, padding: isMobile ? '20px 14px' : '32px 24px', maxWidth: 1080, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
      <PageHeader title="Settings" />
      <p style={{ color: 'var(--muted)', marginBottom: 18, fontSize: 14 }}>Manage your integrations, branding and inbox address. Anything left blank falls back to Howler's defaults.</p>

      {/* Section nav */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 22, flexWrap: 'wrap', borderBottom: '1px solid var(--hairline)', paddingBottom: 10 }}>
        {sections.map(([key, label, icon]) => (
          <button key={key} onClick={() => { setSection(key); setParams((p) => { p.set('section', key); p.delete('focus'); return p; }, { replace: true }); }} style={tabBtn(section === key)}>
            <Icon name={icon} size={14} style={{ marginRight: 6, display: 'inline-block', verticalAlign: '-2px' }} />{label}
          </button>
        ))}
      </div>

      {section === 'team' ? (
        ent ? (
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{ent.name} · Team</h2>
            <TeamManager entityId={ent.id} entityName={ent.name} />
          </div>
        ) : <p style={{ color: 'var(--muted)' }}>Switch to a client to manage its team.</p>
      ) : section === 'notifications' ? (
        <div style={{ maxWidth: 520 }}>
          <p style={hint}>
            Choose how Howler reaches you. The channel and type switches apply to your login across all your
            profiles; the lists under each type are {ent ? <strong>{ent.name}</strong> : 'this client'}’s — switch
            profile in the header to manage another. The in-app inbox always receives messages regardless.
          </p>
          {/* Scoped to the profile in context, like every other section here. */}
          <NotificationPrefs entityId={ent?.id || ''} />
        </div>
      ) : section === 'security' ? (
        <>
          <TwoFactorCard />
          {flagOn(myFlags, 'integrations.api_personal') && <PersonalAiConnectionsCard />}
        </>
      ) : section === 'hidden' ? (
        // Uses scopeId directly rather than the items/activeItem chain below:
        // NavHidden only needs an entityId, and items only lists entities the
        // logged-in user is personally a member of — for a Howler admin who is
        // PREVIEWING a client (not a genuine profile switch), scopeId still
        // resolves via ctx.previewEntityId but activeItem never would, wrongly
        // blocking this page (09-15: Robyn saw "Switch to a client profile...").
        scopeId ? (
          <div style={{ maxWidth: 760 }}>
            <p style={hint}>Tuck a report or a whole event out of your sidebar into a collapsed "Hidden" section — nothing is deleted, and segments or campaigns built on it keep working. This is the same list your team sees and edits from the sidebar itself.</p>
            <NavHidden entityId={scopeId} />
          </div>
        ) : <p style={{ color: 'var(--muted)' }}>Switch to a client to manage its hidden reports.</p>
      ) : !items ? (
        <p style={{ color: 'var(--muted)' }}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={{ color: 'var(--muted)' }}>No client account is linked to your login yet.</p>
      ) : !activeItem ? (
        <p style={{ color: 'var(--muted)' }}>Switch to a client profile to manage its settings.</p>
      ) : (
        <div style={{ marginBottom: 36 }}>
          {items.length > 1 && <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 12 }}>{activeItem.name}</h2>}

          {section === 'integrations' && (
            <div style={{ maxWidth: 680 }}>
              <p style={hint}>Connect your own Looker and Anthropic accounts. Leave blank to use Howler's.</p>
              <div id="int-form" style={{ scrollMarginTop: 80, borderRadius: 12 }}>
              <IntegrationsForm
                value={activeItem}
                lookerActive={false}
                collapsible
                showMeta
                showTikTok
                showGoogleAdsClient
                showSlack
                showChottu
                showQueueit
                showPixel
                pixelEntityId={activeItem.entityId}
                onPixelStatus={() => api.myPixelStatus(activeItem.entityId)}
                onCreatePixelAudiences={(channel) => api.myCreatePixelAudiences(activeItem.entityId, channel)}
                showSocialPlus
                canManageLock={isAdmin || role === 'owner'}
                canLinkSocialAssets={isAdmin}
                lockableKeys={['looker', 'anthropic', 'meta', 'tiktok', 'googleAds', 'slack', 'chottu', 'pixel', 'queueit', 'socialplus']}
                locks={activeItem.locks || {}}
                onTestSlack={() => api.testMySlack(activeItem.entityId)}
                onToggleLock={async (key, locked) => {
                  const v = await api.setMyIntegrationLock(activeItem.entityId, key, locked);
                  setItems((arr) => arr.map((x) => (x.entityId === activeItem.entityId ? { ...x, ...v } : x)));
                }}
                onSave={async (p) => {
                  const v = await api.saveMyIntegrations(activeItem.entityId, p);
                  setItems((arr) => arr.map((x) => (x.entityId === activeItem.entityId ? { ...x, ...v } : x)));
                }}
              />
              </div>
              <ApiKeysCard entityId={activeItem.entityId} scope="my" />
              {flagOn(myFlags, 'integrations.api_dashboards') && <DashboardDraftsCard entityId={activeItem.entityId} scope="my" />}
              <WorkspaceKeyCard scope="my" />
              <div id="int-meta" style={{ scrollMarginTop: 80, borderRadius: 12 }}><MetaConnectCard entityId={activeItem.entityId} scope="my" /></div>
              <TikTokConnectCard entityId={activeItem.entityId} scope="my" />
              <XConnectCard entityId={activeItem.entityId} scope="my" />
              <LinkedInConnectCard entityId={activeItem.entityId} scope="my" />
              <QueueItCard entityId={activeItem.entityId} scope="my" />
              <DriveSourcesCard entityId={activeItem.entityId} scope="my" />
              {flagOn(myFlags, 'integrations.lead_feeds') && <LeadFeedsCard entityId={activeItem.entityId} scope="my" />}
            </div>
          )}

          {section === 'dashboards' && (
            <div style={{ maxWidth: 760 }}>
              <p style={hint}>Attach a dashboard directly to one of your events — no set required. It appears first in that event's sidebar, before the sets.</p>
              <SuiteDashboards entityId={activeItem.entityId} />
            </div>
          )}

          {section === 'sharelinks' && (
            <div style={{ maxWidth: 860 }}>
              <ShareLinksManager entityId={activeItem.entityId} scope="my" />
            </div>
          )}

          {section === 'fanowl' && (
            <div style={{ maxWidth: 760 }}>
              <FanOwlAdmin scope="my" entityId={activeItem.entityId} />
            </div>
          )}

          {/* Partner databases you've been given — read-only on purpose: Howler
              sets the link up, you can see exactly what it lets you do. */}
          {/* Everything on the client's own website: contact forms, newsletter
              sign-ups, pop-ups and the settings they share. */}
          {section === 'website' && (
            <div style={{ maxWidth: 1100 }}>
              <WebsiteHub entityId={activeItem.entityId} scope="my" parts={webParts} initial={webPart} />
            </div>
          )}
          {section === 'shareddb' && (
            <div style={{ maxWidth: 760 }}>
              <SharedDatabasesCard entityId={activeItem.entityId} scope="my" />
            </div>
          )}
          {/* Events another organiser runs that Howler shared with you — read-only. */}
          {section === 'sharedsuites' && (
            <div style={{ maxWidth: 760 }}>
              <SharedSuitesCard entityId={activeItem.entityId} scope="my" />
            </div>
          )}

          {section === 'email' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <BrandingSection title="Account &amp; portfolio" subtitle="your overall look · used on portfolio digests">
                <p style={hint}>Your colours and logo — they style your whole Pulse platform (buttons, accents, charts) and your notification emails. Used everywhere by default, and on portfolio (multi-event) digests. Blank fields keep Howler's defaults.</p>
                <MailTemplateEditor scope="my" entityId={activeItem.entityId} />
              </BrandingSection>

              <BrandingSection title="Your sending domain" subtitle="send from you@yourbrand.com">
                <SendingDomainCard entityId={activeItem.entityId} scope="my" />
              </BrandingSection>

              {flagOn(myFlags, 'linkdomain') && (
                <BrandingSection title="Your link domain" subtitle="forms, surveys &amp; shared reports on pulse.yourbrand.com">
                  <LinkDomainCard entityId={activeItem.entityId} scope="my" />
                </BrandingSection>
              )}

              <BrandingSection title="Events" subtitle="brand a specific event">
                <p style={hint}>Give a specific event its own logo, colours and sender name — used for that event's campaigns and digests, and the in-app look while you're viewing it. Blank fields inherit your account branding above.</p>
                <EventBranding entityId={activeItem.entityId} />
              </BrandingSection>
            </div>
          )}

          {section === 'billing' && (
            <div style={{ maxWidth: 760 }}>
              <p style={hint}>Your card on file, billing details and monthly invoices — each one shows exactly how its per-ticket fee and messaging were counted.</p>
              <BillingCard scope="my" entityId={activeItem.entityId} />
            </div>
          )}

          {section === 'fees' && (
            <div style={{ maxWidth: 680 }}>
              <p style={hint}>Your per-message campaign rates and what you’ve spent so far.</p>
              <RateCard scope="my" entityId={activeItem.entityId} />
            </div>
          )}

          {section === 'sentmail' && (
            <div>
              <p style={hint}>Every email sent on your behalf — digests, campaigns and notifications — and what's scheduled next.</p>
              <MailLogView load={(params) => api.getMyMailLog(activeItem.entityId, params)} entityId={activeItem.entityId} />
            </div>
          )}

          {section === 'inbox' && (
            <div style={{ maxWidth: 680 }}>
              <p style={hint}>CC this address on any email and the conversation is captured in your Pulse inbox — nothing lives only in someone's mailbox.</p>
              <OwlAddressCard entityId={activeItem.entityId} />
            </div>
          )}

          {section === 'owlmemory' && (
            <div style={{ maxWidth: 680 }}>
              <p style={hint}>What the Owl remembers about your account, your events and you — so it doesn't re-ask what it already knows.</p>
              <MyOwlMemory entityId={activeItem.entityId} />
            </div>
          )}
        </div>
      )}
    </main>
  );
}

const tabBtn = (active) => ({
  padding: '8px 14px', borderRadius: 980, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  border: active ? '1.5px solid var(--brand)' : '1.5px solid var(--hairline)',
  background: active ? 'rgba(var(--brand-rgb), 0.08)' : 'transparent',
  color: active ? 'var(--brand)' : 'var(--text)',
});
const hint = { color: 'var(--muted)', fontSize: 13, marginBottom: 14, lineHeight: 1.5 };
// A branding sub-section as a clearly-expandable white card (the old <details>
// hid its disclosure triangle behind a flex summary, so it read as a dead
// heading). Open by default so the controls — and the fact it expands — are
// obvious; collapsible via the chevron to keep the page tidy. Used for both the
// account branding and the per-event branding so they match.
function BrandingSection({ title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={acctCard}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={acctHeader} aria-expanded={open}>
        <span style={{ ...acctChevron, transform: open ? 'rotate(90deg)' : 'none' }}>▸</span>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{title}</span>
        {subtitle && <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>{subtitle}</span>}
      </button>
      {open && <div style={{ padding: '0 14px 14px' }}>{children}</div>}
    </div>
  );
}
const acctCard = { border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden', background: 'var(--card)' };
const acctHeader = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', width: '100%', textAlign: 'left', cursor: 'pointer', background: 'transparent', border: 'none', padding: '13px 14px', color: 'var(--text)' };
const acctChevron = { color: 'var(--muted)', fontSize: 12, transition: 'transform 0.15s', flexShrink: 0 };

// Per-event branding self-service: pick one of this client's events, then edit
// its branding override (inherits the account branding above where left blank).
function EventBranding({ entityId }) {
  const [suites, setSuites] = useState(null);
  const [picked, setPicked] = useState('');
  useEffect(() => {
    api.mySuites().then((all) => setSuites((all || []).filter((s) => s.entityId === entityId))).catch(() => setSuites([]));
  }, [entityId]);

  if (!suites) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading your events…</p>;
  if (!suites.length) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>No events yet — once Howler sets up an event for you, you can brand it here.</p>;

  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Event</label>
      <select value={picked} onChange={(e) => setPicked(e.target.value)} style={selectBox}>
        <option value="">Choose an event…</option>
        {suites.map((s) => {
          const icon = s.icon && !s.icon.startsWith('data:') ? `${s.icon} ` : '';
          return <option key={s.id} value={s.id}>{icon}{s.name}</option>;
        })}
      </select>
      {picked && <div style={{ marginTop: 16 }}><MailTemplateEditor key={picked} scope="my-suite" entityId={entityId} suiteId={picked} /></div>}
    </div>
  );
}

// Suite-level dashboards self-service: pick one of this client's events, then
// attach/detach dashboards directly to it (mirrors the admin suite editor).
function SuiteDashboards({ entityId }) {
  const [suites, setSuites] = useState(null);
  const [picked, setPicked] = useState('');
  useEffect(() => {
    api.mySuites().then((all) => setSuites((all || []).filter((s) => s.entityId === entityId))).catch(() => setSuites([]));
  }, [entityId]);

  if (!suites) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading your events…</p>;
  if (!suites.length) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>No events yet — once Howler sets up an event for you, you can add dashboards here.</p>;

  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Event</label>
      <select value={picked} onChange={(e) => setPicked(e.target.value)} style={selectBox}>
        <option value="">Choose an event…</option>
        {suites.map((s) => {
          const icon = s.icon && !s.icon.startsWith('data:') ? `${s.icon} ` : '';
          return <option key={s.id} value={s.id}>{icon}{s.name}</option>;
        })}
      </select>
      {picked && <div style={{ marginTop: 16 }}><SuiteDashboardsEditor key={picked} suiteId={picked} scope="my" /></div>}
    </div>
  );
}

// Hidden reports self-service: pick one of this client's events, then
// hide/unhide it (or its reports) from the sidebar (mirrors the admin suite editor).
function NavHidden({ entityId }) {
  const [suites, setSuites] = useState(null);
  const [picked, setPicked] = useState('');
  useEffect(() => {
    api.mySuites().then((all) => setSuites((all || []).filter((s) => s.entityId === entityId))).catch(() => setSuites([]));
  }, [entityId]);

  if (!suites) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading your events…</p>;
  if (!suites.length) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>No events yet — once Howler sets up an event for you, you can hide its reports here.</p>;

  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Event</label>
      <select value={picked} onChange={(e) => setPicked(e.target.value)} style={selectBox}>
        <option value="">Choose an event…</option>
        {suites.map((s) => {
          const icon = s.icon && !s.icon.startsWith('data:') ? `${s.icon} ` : '';
          return <option key={s.id} value={s.id}>{icon}{s.name}{s.hidden ? ' (hidden)' : ''}</option>;
        })}
      </select>
      {picked && <div style={{ marginTop: 16 }}><NavHiddenEditor key={picked} suiteId={picked} entityId={entityId} scope="my" /></div>}
    </div>
  );
}

const selectBox = {
  padding: '9px 12px', borderRadius: 10, fontSize: 14, border: '1.5px solid var(--hairline)',
  background: 'var(--surface)', color: 'var(--text)', minWidth: 260, maxWidth: '100%',
};
